/**
 * Ownership reconciliation, as the screen needs it.
 *
 * Three calls, three capabilities, on purpose: reading and saving a mapping
 * goes through ordinary RLS-policed table access on `team.manage`;
 * previewing and applying go through `owner_codes_summary()` /
 * `preview_reconciliation()` / `apply_reconciliation()`, each re-checking
 * `customers.assign` by hand server-side, because those need a company-wide
 * view an invoker function scoped to the caller's own visible rows would not
 * give a Manager who cannot see the whole book by any other capability.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

export interface OwnerCodeSummary {
    importedCode: string;
    customerCount: number;
    mappedMemberId: string | null;
    mappedMemberName: string | null;
    mappedMemberStatus: string | null;
    suggestedMemberId: string | null;
    suggestedMemberName: string | null;
}

interface SummaryRow {
    imported_code: string; customer_count: number;
    mapped_member_id: string | null; mapped_member_name: string | null; mapped_member_status: string | null;
    suggested_member_id: string | null; suggested_member_name: string | null;
}

export function useOwnerCodesSummary(workspaceId: string) {
    const [codes, setCodes] = useState<OwnerCodeSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const issuedFor = useRef(workspaceId);

    const load = useCallback(async () => {
        issuedFor.current = workspaceId;
        setLoading(true);
        setError(null);
        const { data, error: loadError } = await supabase().rpc('owner_codes_summary', { p_workspace: workspaceId });
        if (issuedFor.current !== workspaceId) return;
        if (loadError) {
            setError(/insufficient_privilege|permission/i.test(loadError.message)
                ? 'You do not have permission to see ownership codes.'
                : 'We could not load the ownership codes. Try again in a moment.');
            setCodes([]);
        } else {
            setCodes((data as SummaryRow[]).map(r => ({
                importedCode: r.imported_code, customerCount: r.customer_count,
                mappedMemberId: r.mapped_member_id, mappedMemberName: r.mapped_member_name,
                mappedMemberStatus: r.mapped_member_status,
                suggestedMemberId: r.suggested_member_id, suggestedMemberName: r.suggested_member_name,
            })));
        }
        setLoading(false);
    }, [workspaceId]);

    useEffect(() => { void load(); }, [load]);

    return { codes, loading, error, reload: load };
}

export class MappingError extends Error {}

const permissionMessage = (message: string): string =>
    /row-level security|permission denied|insufficient_privilege/i.test(message)
        ? 'You do not have permission to manage ownership mappings.'
        : 'We could not save that. Try again in a moment.';

export async function setOwnerMapping(workspaceId: string, importedCode: string, memberId: string): Promise<void> {
    const { error } = await supabase()
        .from('workspace_owner_mappings')
        .upsert({ workspace_id: workspaceId, imported_code: importedCode, member_id: memberId }, { onConflict: 'workspace_id,imported_code' });
    if (error) throw new MappingError(permissionMessage(error.message));
}

export async function removeOwnerMapping(workspaceId: string, importedCode: string): Promise<void> {
    const { error } = await supabase()
        .from('workspace_owner_mappings')
        .delete().eq('workspace_id', workspaceId).ilike('imported_code', importedCode);
    if (error) throw new MappingError(permissionMessage(error.message));
}

export interface ReconciliationCandidate {
    customerId: string;
    company: string;
    importedCode: string;
    currentOwnerId: string | null;
    currentOwnerName: string | null;
    proposedOwnerId: string;
    proposedOwnerName: string;
    updatedAt: string;
}

interface PreviewRow {
    customer_id: string; company: string; imported_code: string;
    current_owner_member_id: string | null; current_owner_name: string | null;
    proposed_owner_member_id: string; proposed_owner_name: string; updated_at: string;
}

export async function previewReconciliation(workspaceId: string): Promise<ReconciliationCandidate[]> {
    const { data, error } = await supabase().rpc('preview_reconciliation', { p_workspace: workspaceId });
    if (error) throw new MappingError(permissionMessage(error.message));
    return (data as PreviewRow[]).map(r => ({
        customerId: r.customer_id, company: r.company, importedCode: r.imported_code,
        currentOwnerId: r.current_owner_member_id, currentOwnerName: r.current_owner_name,
        proposedOwnerId: r.proposed_owner_member_id, proposedOwnerName: r.proposed_owner_name,
        updatedAt: r.updated_at,
    }));
}

export type ApplyStatus = 'applied' | 'conflicted' | 'skipped_unmapped' | 'unchanged';

export interface ApplyResult {
    customerId: string;
    status: ApplyStatus;
    detail: string | null;
}

export async function applyReconciliation(
    workspaceId: string, candidates: readonly ReconciliationCandidate[],
): Promise<ApplyResult[]> {
    const payload = candidates.map(c => ({ customer_id: c.customerId, expected_updated_at: c.updatedAt }));
    const { data, error } = await supabase()
        .rpc('apply_reconciliation', { p_workspace: workspaceId, p_customers: payload });
    if (error) throw new MappingError(permissionMessage(error.message));
    return data as ApplyResult[];
}
